import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "semantic-ui-react";

import { Category } from "../../../../../api";

import styles from "./CreateCategoryPage.module.scss";

const INITIAL_VALUES = {
  id: "",
  name: "",
  slug: "",
  parent: "",
  description: "",
  order: 1,
  visible: true,
  seo: {
    metaTitle: "",
    metaDescription: "",
  },
  image: null,
};

const MAX_IMAGE_SIZE_IN_BYTES = 5 * 1024 * 1024;

const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
];

function createSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function createCategoryId(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/Ñ/g, "N")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getCategoriesResponse(result) {
  const response = result?.categories || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response.docs;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
}

function getCategoryId(category) {
  return String(category?._id || category?.id || "");
}

function getParentId(parent) {
  if (!parent) {
    return "";
  }

  if (typeof parent === "object") {
    return String(parent._id || parent.id || "");
  }

  return String(parent);
}

function getParentDisplayName(category) {
  const level = Math.max(Number(category?.level || 1) - 1, 0);
  const prefix = level > 0 ? `${"— ".repeat(level)}` : "";

  return `${prefix}${category?.name || ""}`;
}

function isAcceptedImage(file) {
  return ACCEPTED_IMAGE_TYPES.includes(file?.type);
}

export function CreateCategoryPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(INITIAL_VALUES);
  const [categories, setCategories] = useState([]);
  const [imagePreview, setImagePreview] = useState("");
  const [hasImagePreviewError, setHasImagePreviewError] = useState(false);

  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [validationErrors, setValidationErrors] = useState({});
  const [serverError, setServerError] = useState("");

  // Mientras el usuario no haya tocado el campo "Orden" a mano, lo
  // mantenemos siguiendo la sugerencia automática (ver suggestedOrder
  // abajo) -- apenas escribe un valor propio, dejamos de tocarlo aunque
  // después cambie la categoría padre.
  const [orderTouchedByUser, setOrderTouchedByUser] = useState(false);

  useEffect(() => {
    async function loadCategories() {
      try {
        setIsLoadingCategories(true);
        setServerError("");

        const categoryController = new Category();
        const result = await categoryController.getCategories(1, 100);

        setCategories(getCategoriesResponse(result));
      } catch (errorResponse) {
        console.error("loadCategories error:", errorResponse);

        setCategories([]);

        setServerError(
          errorResponse?.msg ||
            "No fue posible cargar las categorías disponibles."
        );
      } finally {
        setIsLoadingCategories(false);
      }
    }

    loadCategories();
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const parentCategories = useMemo(() => {
    return categories.filter(
      (category) => Number(category.level || 1) < 3
    );
  }, [categories]);

  const selectedParent = useMemo(() => {
    return parentCategories.find(
      (category) => getCategoryId(category) === String(formData.parent)
    );
  }, [parentCategories, formData.parent]);

  const newCategoryLevel = formData.parent
    ? Number(selectedParent?.level || 0) + 1
    : 1;

  // El orden solo compite entre categorías HERMANAS (misma categoría
  // padre, o mismo nivel principal si no hay padre) -- así lo ordena
  // Category.js/EditCategoryPage.js al mostrar los hijos de una
  // categoría. La sugerencia es "el siguiente después del mayor que ya
  // existe en ese mismo grupo", para que la persona no tenga que revisar
  // manualmente qué números ya están usados.
  const siblingCategories = useMemo(() => {
    return categories.filter(
      (category) => getParentId(category.parent) === (formData.parent || "")
    );
  }, [categories, formData.parent]);

  const suggestedOrder = useMemo(() => {
    if (siblingCategories.length === 0) {
      return 1;
    }

    const maxOrder = siblingCategories.reduce(
      (max, category) => Math.max(max, Number(category.order) || 1),
      0
    );

    return maxOrder + 1;
  }, [siblingCategories]);

  useEffect(() => {
    if (orderTouchedByUser || isLoadingCategories) {
      return;
    }

    setFormData((currentData) =>
      currentData.order === suggestedOrder
        ? currentData
        : { ...currentData, order: suggestedOrder }
    );
  }, [suggestedOrder, orderTouchedByUser, isLoadingCategories]);

  function clearFieldError(fieldName) {
    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;

    if (name === "metaTitle" || name === "metaDescription") {
      setFormData((currentData) => ({
        ...currentData,
        seo: {
          ...currentData.seo,
          [name]: value,
        },
      }));

      clearFieldError(name);
      return;
    }

    if (name === "order") {
      setOrderTouchedByUser(true);
    }

    setFormData((currentData) => ({
      ...currentData,
      [name]: type === "checkbox" ? checked : value,
    }));

    clearFieldError(name);
  }

  function handleUseSuggestedOrder() {
    setOrderTouchedByUser(false);

    setFormData((currentData) => ({
      ...currentData,
      order: suggestedOrder,
    }));

    clearFieldError("order");
  }

  function handleNameChange(event) {
    const name = event.target.value;

    setFormData((currentData) => ({
      ...currentData,
      name,
      id: currentData.id || createCategoryId(name),
      slug: currentData.slug || createSlug(name),
    }));

    setValidationErrors((currentErrors) => ({
      ...currentErrors,
      name: "",
      slug: "",
    }));
  }

  function handleOpenImagePicker() {
    if (!isSubmitting) {
      fileInputRef.current?.click();
    }
  }

  function handleImageChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!isAcceptedImage(file)) {
      setServerError(
        "Selecciona una imagen en formato JPG, PNG o WEBP."
      );
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_IN_BYTES) {
      setServerError("La imagen no puede superar 5 MB.");
      event.target.value = "";
      return;
    }

    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    const previewUrl = URL.createObjectURL(file);

    setImagePreview(previewUrl);
    setHasImagePreviewError(false);

    setFormData((currentData) => ({
      ...currentData,
      image: file,
    }));

    setServerError("");
    clearFieldError("image");
  }

  function handleImagePreviewError() {
    setHasImagePreviewError(true);
    setServerError(
      "No fue posible mostrar la vista previa de la imagen seleccionada."
    );
  }

  function handleRemoveImage() {
    if (imagePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setImagePreview("");
    setHasImagePreviewError(false);

    setFormData((currentData) => ({
      ...currentData,
      image: null,
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function validateForm() {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "El nombre de la categoría es obligatorio.";
    } else if (formData.name.trim().length > 150) {
      errors.name = "El nombre no puede superar 150 caracteres.";
    }

    if (!formData.slug.trim()) {
      errors.slug = "El slug o URL de la categoría es obligatorio.";
    } else if (createSlug(formData.slug).length > 160) {
      errors.slug = "El slug no puede superar 160 caracteres.";
    }

    if (
      !Number.isInteger(Number(formData.order)) ||
      Number(formData.order) < 1
    ) {
      errors.order =
        "El orden debe ser un número entero mayor o igual a 1.";
    }

    if (formData.parent && !selectedParent) {
      errors.parent = "La categoría padre seleccionada no está disponible.";
    }

    if (formData.parent && newCategoryLevel > 3) {
      errors.parent = "No se permite superar 3 niveles de categorías.";
    }

    if (formData.description.length > 1000) {
      errors.description =
        "La descripción no puede superar 1,000 caracteres.";
    }

    if (formData.seo.metaTitle.length > 60) {
      errors.metaTitle =
        "El título SEO no puede superar 60 caracteres.";
    }

    if (formData.seo.metaDescription.length > 160) {
      errors.metaDescription =
        "La descripción meta no puede superar 160 caracteres.";
    }

    if (formData.image && !isAcceptedImage(formData.image)) {
      errors.image = "La imagen debe ser JPG, PNG o WEBP.";
    }

    if (
      formData.image &&
      formData.image.size > MAX_IMAGE_SIZE_IN_BYTES
    ) {
      errors.image = "La imagen no puede superar 5 MB.";
    }

    return errors;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setServerError("");
      return;
    }

    try {
      setIsSubmitting(true);
      setServerError("");

      const categoryController = new Category();

      await categoryController.createCategory({
        ...formData,
        id: formData.id.trim() || createCategoryId(formData.name),
        slug: createSlug(formData.slug || formData.name),
        parent: formData.parent || null,
        order: Number(formData.order),
      });

      navigate("/admin/categories", {
        state: {
          success: "Categoría creada correctamente.",
        },
      });
    } catch (errorResponse) {
      console.error("createCategory error:", errorResponse);

      if (errorResponse?.errors) {
        setValidationErrors(errorResponse.errors);
      }

      setServerError(
        errorResponse?.msg ||
          "No fue posible crear la categoría. Intenta nuevamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    if (!isSubmitting) {
      navigate("/admin/categories");
    }
  }

  const hasValidationErrors = Object.values(validationErrors).some(Boolean);

  return (
    <section className={styles.CreateCategoryPage}>
      <header className={styles.PageHeader}>
        <div className={styles.TitleSection}>
          <button
            type="button"
            className={styles.BackButton}
            onClick={handleCancel}
            disabled={isSubmitting}
            aria-label="Volver a categorías"
          >
            ←
          </button>

          <div>
            <p className={styles.Breadcrumb}>
              Categorías / Nueva categoría
            </p>

            <h1>Nueva categoría</h1>

            <p className={styles.Subtitle}>
              Crea una categoría principal o asigna una categoría padre para
              crear una subcategoría.
            </p>
          </div>
        </div>

        <div className={styles.HeaderActions}>
          <button
            type="button"
            className={styles.CancelButton}
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>

          <button
            type="submit"
            form="create-category-form"
            className={styles.PublishButton}
            disabled={isSubmitting || isLoadingCategories}
          >
            {isSubmitting ? "Guardando..." : "Guardar categoría"}
          </button>
        </div>
      </header>

      {hasValidationErrors && (
        <div className={styles.ValidationMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <div>
            <strong>Revisa los campos requeridos.</strong>

            <span>
              Corrige los errores marcados antes de guardar la categoría.
            </span>
          </div>
        </div>
      )}

      {serverError && (
        <div className={styles.ServerErrorMessage} role="alert">
          <span className={styles.MessageIcon} aria-hidden="true">
            !
          </span>

          <span>{serverError}</span>
        </div>
      )}

      <form
        id="create-category-form"
        className={styles.FormContent}
        onSubmit={handleSubmit}
      >
        <section className={styles.CategoryBasicInformation}>
          <header className={styles.SectionHeader}>
            <div>
              <h2>Información básica</h2>

              <p>Define los datos principales de la categoría.</p>
            </div>
          </header>

          <div className={styles.InfoMessage}>
            <span className={styles.InfoIcon}>i</span>

            <p>
              Deja la categoría padre vacía para crear una categoría principal.
              Selecciona una categoría padre para crear una subcategoría.
            </p>
          </div>

          <div className={styles.FormGrid}>
            <div className={styles.FormField}>
              <label htmlFor="name">
                Nombre <span>*</span>
              </label>

              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="Ej. Bolsas de mano"
                error={Boolean(validationErrors.name)}
                disabled={isSubmitting}
              />

              {validationErrors.name && (
                <span className={styles.FieldError}>
                  {validationErrors.name}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="slug">
                Slug / URL <span>*</span>
              </label>

              <Input
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="Ej. bolsas-de-mano"
                error={Boolean(validationErrors.slug)}
                disabled={isSubmitting}
              />

              {validationErrors.slug ? (
                <span className={styles.FieldError}>
                  {validationErrors.slug}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Se usará en la URL de la tienda.
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="parent">Categoría padre</label>

              <select
                id="parent"
                name="parent"
                value={formData.parent}
                onChange={handleChange}
                className={validationErrors.parent ? styles.error : ""}
                disabled={isSubmitting || isLoadingCategories}
              >
                <option value="">
                  {isLoadingCategories
                    ? "Cargando categorías..."
                    : "Sin categoría padre (categoría principal)"}
                </option>

                {parentCategories.map((category) => (
                  <option
                    key={getCategoryId(category)}
                    value={getCategoryId(category)}
                  >
                    {getParentDisplayName(category)}
                  </option>
                ))}
              </select>

              {validationErrors.parent ? (
                <span className={styles.FieldError}>
                  {validationErrors.parent}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  {formData.parent
                    ? `Se creará como categoría de nivel ${newCategoryLevel}.`
                    : "Déjala vacía para crear una categoría principal."}
                </span>
              )}
            </div>

            <div className={styles.FormField}>
              <label htmlFor="image">Imagen de portada</label>

              <div className={styles.ImageUpload}>
                {imagePreview ? (
                  <div className={styles.ImagePreview}>
                    {!hasImagePreviewError && (
                      <img
                        src={imagePreview}
                        alt="Vista previa de la categoría"
                        onError={handleImagePreviewError}
                      />
                    )}

                    {hasImagePreviewError && (
                      <div className={styles.ImageFallback}>
                        <span aria-hidden="true">▣</span>

                        <p>No fue posible mostrar la imagen.</p>

                        <button
                          type="button"
                          className={styles.SelectAnotherImageButton}
                          onClick={handleOpenImagePicker}
                          disabled={isSubmitting}
                        >
                          Seleccionar otra imagen
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      className={styles.RemoveImageButton}
                      onClick={handleRemoveImage}
                      disabled={isSubmitting}
                    >
                      Eliminar imagen
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.UploadLabel}
                    onClick={handleOpenImagePicker}
                    disabled={isSubmitting}
                  >
                    <span aria-hidden="true">↥</span>

                    <span>
                      <strong>Subir imagen de portada</strong>
                      <small>
                        JPG, PNG o WEBP. Máximo 5 MB.
                      </small>
                    </span>
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                id="image"
                name="image"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                disabled={isSubmitting}
                hidden
              />

              {validationErrors.image ? (
                <span className={styles.FieldError}>
                  {validationErrors.image}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Recomendado: 1200 × 1200 px. La imagen se ajusta y recorta
                  automáticamente al formato cuadrado.
                </span>
              )}
            </div>

            <div className={`${styles.FormField} ${styles.FullWidth}`}>
              <label htmlFor="description">Descripción</label>

              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={
                  validationErrors.description ? styles.error : ""
                }
                placeholder="Describe la categoría y el tipo de productos que contiene..."
                maxLength="1000"
                disabled={isSubmitting}
              />

              <div className={styles.TextAreaFooter}>
                {validationErrors.description ? (
                  <span className={styles.FieldError}>
                    {validationErrors.description}
                  </span>
                ) : (
                  <span />
                )}

                <span>{formData.description.length} / 1000</span>
              </div>
            </div>

            <div className={styles.FormField}>
              <label htmlFor="order">
                Orden <span>*</span>
              </label>

              <Input
                id="order"
                name="order"
                type="number"
                min="1"
                step="1"
                value={formData.order}
                onChange={handleChange}
                error={Boolean(validationErrors.order)}
                disabled={isSubmitting}
              />

              {validationErrors.order ? (
                <span className={styles.FieldError}>
                  {validationErrors.order}
                </span>
              ) : (
                <span className={styles.FieldHelp}>
                  Menor número = aparece primero. Siguiente disponible
                  {formData.parent
                    ? " en esta categoría padre"
                    : " entre las categorías principales"}
                  : {suggestedOrder}.{" "}
                  {Number(formData.order) !== suggestedOrder && (
                    <button
                      type="button"
                      className={styles.UseSuggestedOrderButton}
                      onClick={handleUseSuggestedOrder}
                      disabled={isSubmitting}
                    >
                      Usar {suggestedOrder}
                    </button>
                  )}
                </span>
              )}
            </div>

            <div className={styles.VisibilityField}>
              <span>Visible</span>

              <label className={styles.VisibilitySwitch}>
                <input
                  name="visible"
                  type="checkbox"
                  checked={formData.visible}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />

                <span />
              </label>

              <small>La categoría será visible en la tienda.</small>
            </div>

            <section className={`${styles.SeoSection} ${styles.FullWidth}`}>
              <h3 className={styles.SeoTitle}>SEO</h3>

              <p className={styles.SeoDescription}>
                Optimiza cómo se mostrará esta categoría en buscadores.
              </p>

              <div className={styles.SeoGrid}>
                <div className={styles.FormField}>
                  <label htmlFor="metaTitle">Título SEO</label>

                  <Input
                    id="metaTitle"
                    name="metaTitle"
                    value={formData.seo.metaTitle}
                    onChange={handleChange}
                    error={Boolean(validationErrors.metaTitle)}
                    disabled={isSubmitting}
                  />

                  <div className={styles.TextAreaFooter}>
                    {validationErrors.metaTitle ? (
                      <span className={styles.FieldError}>
                        {validationErrors.metaTitle}
                      </span>
                    ) : (
                      <span />
                    )}

                    <span>{formData.seo.metaTitle.length} / 60</span>
                  </div>
                </div>

                <div className={styles.FormField}>
                  <label htmlFor="metaDescription">
                    Descripción meta
                  </label>

                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    value={formData.seo.metaDescription}
                    onChange={handleChange}
                    className={
                      validationErrors.metaDescription
                        ? styles.error
                        : ""
                    }
                    maxLength="160"
                    disabled={isSubmitting}
                  />

                  <div className={styles.TextAreaFooter}>
                    {validationErrors.metaDescription ? (
                      <span className={styles.FieldError}>
                        {validationErrors.metaDescription}
                      </span>
                    ) : (
                      <span />
                    )}

                    <span>
                      {formData.seo.metaDescription.length} / 160
                    </span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </form>
    </section>
  );
}